import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabase";
import { SIGNUP_COOLDOWN_KEY, signupConfirmRedirectUrl } from "../../lib/signupOnboarding";
import { readCooldown, startCooldown } from "../../lib/resendCooldown";
import { reportError } from "../../lib/errorReporter";

const STEPS = ["step_1", "step_2", "step_3", "step_4", "step_5"];

export default function SignupCheckEmail({ email }) {
  const { t } = useTranslation();
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(() => readCooldown(SIGNUP_COOLDOWN_KEY));
  const inFlight = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown(readCooldown(SIGNUP_COOLDOWN_KEY)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const resend = useCallback(async () => {
    // Synchronous duplicate-submit guard: a second press can never start a
    // second request, however fast it happens.
    if (inFlight.current || busy || cooldown > 0 || !email) return;
    inFlight.current = true;
    setBusy(true);
    const { error: resendError } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: signupConfirmRedirectUrl() },
    });
    if (resendError) {
      // Never surfaced: the outcome must stay neutral and the raw provider
      // error is never rendered and never logged.
      reportError("auth.signup_resend");
    }
    inFlight.current = false;
    setBusy(false);
    startCooldown(SIGNUP_COOLDOWN_KEY);
    setCooldown(readCooldown(SIGNUP_COOLDOWN_KEY));
    setResent(true);
  }, [busy, cooldown, email]);

  return (
    <div className="qw-auth-form" data-testid="signup-check-email">
      <h1>{t("signup.check_email_title")}</h1>
      <p className="lead" data-testid="signup-check-email-body">
        {t("signup.check_email_body")}
      </p>

      {email && (
        <div style={{ marginBottom: 14 }}>
          <div className="qw-help">{t("signup.check_email_sent_to")}</div>
          <div style={{ fontWeight: 600 }} data-testid="signup-check-email-address">
            {email}
          </div>
        </div>
      )}

      <div className="qw-help" style={{ marginBottom: 6 }}>
        {t("signup.steps_title")}
      </div>
      <ol
        style={{
          listStyle: "decimal",
          listStylePosition: "outside",
          margin: "0 0 14px 20px",
          padding: 0,
          color: "#334155",
          fontSize: 14,
          lineHeight: 1.7,
        }}
        data-testid="signup-check-email-steps"
      >
        {STEPS.map((key) => (
          <li key={key} data-testid={`signup-check-email-${key.replace("_", "-")}`}>
            {t(`signup.${key}`)}
          </li>
        ))}
      </ol>

      <div
        className="qw-card"
        style={{ background: "#f0fdfa", borderColor: "#99f6e4", color: "#0f766e", marginBottom: 14 }}
        data-testid="signup-check-email-org-note"
      >
        {t("signup.org_note")}
      </div>

      <button
        type="button"
        className="qw-btn primary stretch"
        style={{ width: "100%", justifyContent: "center" }}
        onClick={resend}
        disabled={busy || cooldown > 0 || !email}
        data-testid="signup-resend-confirmation"
      >
        {busy ? t("signup.resending") : t("signup.resend")}
      </button>

      {resent && (
        <div className="qw-help" style={{ marginTop: 10 }} role="status" data-testid="signup-resent-notice">
          {t("signup.resent")}
        </div>
      )}

      {cooldown > 0 && (
        <div className="qw-help" style={{ marginTop: 6 }} data-testid="signup-resend-cooldown">
          {t("signup.cooldown", { seconds: cooldown })}
        </div>
      )}

      <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
        <Link to="/login" data-testid="signup-back-to-login" style={{ color: "#0f766e", fontWeight: 600 }}>
          {t("signup.back_to_login")}
        </Link>
      </div>
    </div>
  );
}
